import { useState } from 'react';
import { ChevronDown, ChevronUp, BookOpen, Sparkles, Heart, Download, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const downloadScripturePdf = async (path, token) => {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await axios.get(`${API_URL}${path}`, { headers, responseType: 'blob' });
  const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = match ? match[1] : 'Scripture_Insight.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

/**
 * ScriptureInsight - Collapsible panel displaying Bible quotation meaning and significance
 * @param {Object} props
 * @param {string} props.meaning - The theological meaning/explanation (300-500 words)
 * @param {string} props.significance - Practical life application (150-250 words)
 * @param {string} props.previewText - The Bible quotation text for reference
 * @param {string} props.className - Additional CSS classes
 */
export const ScriptureInsight = ({ 
  meaning, 
  significance, 
  previewText,
  onDownloadPdf,
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownloadPdf();
      toast.success('Scripture Insight PDF downloaded');
    } catch (e) {
      toast.error('Could not create the PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Don't render if no content
  if (!meaning && !significance) {
    return null;
  }

  return (
    <div className={`scripture-insight card-surface rounded-lg overflow-hidden relative z-10 bg-[var(--bg-surface)] ${className}`} data-testid="scripture-insight">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-[#220F13]/50 transition-colors bg-[var(--bg-card)]"
        data-testid="scripture-insight-toggle"
        aria-expanded={isExpanded}
        aria-controls="scripture-insight-content"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center gothic-border rounded-lg">
            <BookOpen className="w-5 h-5 text-[#D4AF37]" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-cinzel font-semibold text-[#F4EFE6]">
              Scripture Insight
            </h3>
            <p className="text-sm text-[#C2B49A] font-serif">
              {isExpanded ? 'Click to collapse' : 'Click to reveal meaning & significance'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-[#D4AF37]" />
          ) : (
            <ChevronDown className="w-5 h-5 text-[#D4AF37]" />
          )}
        </div>
      </button>

      {/* Expandable Content */}
      <div 
        id="scripture-insight-content"
        className={`transition-all duration-500 ease-in-out ${
          isExpanded ? 'opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
        aria-hidden={!isExpanded}
        inert={isExpanded ? undefined : ''}
      >
        <div className="px-6 pb-6 space-y-6">
          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
          
          {/* Scripture Reference */}
          {previewText && (
            <div className="bg-[#160A0D] rounded-lg p-4 border border-[#D4AF37]/20">
              <p className="text-sm font-cinzel text-[#D4AF37] uppercase tracking-wider mb-2">
                Scripture Reference
              </p>
              <p className="text-[#F4EFE6] font-serif italic leading-relaxed">
                "{previewText}"
              </p>
            </div>
          )}

          {/* Meaning Section */}
          {meaning && (
            <div className="space-y-3" data-testid="scripture-meaning">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                <h4 className="text-base font-cinzel font-semibold text-[#F4EFE6] uppercase tracking-wider">
                  Meaning & Context
                </h4>
              </div>
              <div className="pl-7">
                <p className="text-[#C2B49A] font-serif leading-relaxed whitespace-pre-line">
                  {meaning}
                </p>
              </div>
            </div>
          )}

          {/* Significance Section */}
          {significance && (
            <div className="space-y-3" data-testid="scripture-significance">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#D4AF37]" />
                <h4 className="text-base font-cinzel font-semibold text-[#F4EFE6] uppercase tracking-wider">
                  Significance in Daily Life
                </h4>
              </div>
              <div className="pl-7">
                <p className="text-[#C2B49A] font-serif leading-relaxed whitespace-pre-line">
                  {significance}
                </p>
              </div>
            </div>
          )}

          {/* Footer decoration */}
          {onDownloadPdf && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-cinzel text-sm tracking-wider disabled:opacity-60"
                data-testid="scripture-pdf-download-btn"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {downloading ? 'Preparing PDF...' : 'Download PDF'}
              </button>
            </div>
          )}
          <div className="flex items-center justify-center gap-4 pt-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#D4AF37]/30" />
            <span className="text-[#D4AF37] text-lg">✦</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#D4AF37]/30" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScriptureInsight;
